/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/rootReducer";

/**
 * Custom hook to get the role.
 *
 * @returns {() => string} A function that returns the role.
 */
export const useRoleType = () => {
  const [profileInfo, setProfileInfo] = useState<any>();

  const AgentProfileDetails = useSelector(
    (state: RootState) => state.Dashboard.AgentProfileDetails
  );

  const userProfile = getAgentProfile(AgentProfileDetails);

  useEffect(() => {
    if (userProfile) {
      setProfileInfo(userProfile);
    }
  }, [userProfile]);

  return () => profileInfo?.user_type?.toLowerCase();
};

/**
 * Custom hook to get the language.
 *
 * @returns {() => string} A function that returns the language.
 */
export const useLanguageCode = () => {
  const [language, setLanguage] = useState<any>();

  const AgentProfileDetails = useSelector(
    (state: RootState) => state.Dashboard.AgentProfileDetails
  );

  const languageCode = getAgentProfile(AgentProfileDetails);

  useEffect(() => {
    if (languageCode) {
      setLanguage(languageCode);
      localStorage.setItem(
        "languageCode",
        language?.language_code?.toLowerCase()
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode]);

  return () => language?.language_code?.toLowerCase();
};

/**
 * Custom hook to check if a user has specific permissions.
 *
 * @returns {(permissionValue: string) => boolean} A function that checks if the user has a specific permission.
 */
export const useHasPermission = () => {
  const AgentProfileDetails = useSelector(
    (state: RootState) => state.Dashboard.AgentProfileDetails
  );

  const userProfile = getAgentProfile(AgentProfileDetails);
  const permissionList = getPermissionList(userProfile);

  return (permissionValue: string) =>
    permissionList?.includes(permissionValue?.toLowerCase());
};

/**
 * Extracts the agent profile from the agent profile details.
 *
 * @param {any} AgentProfileDetails - The agent profile details.
 * @returns {any} The agent profile.
 */
const getAgentProfile = (AgentProfileDetails: any) => {
  if (
    AgentProfileDetails?.data?.agentProfile?.successTypeCode?.toLowerCase() ===
    "success"
  ) {
    return AgentProfileDetails?.data?.agentProfile?.responseData?.[0];
  }
  return null;
};

/**
 * Extracts the permission list from the user profile.
 *
 * @param {any} userProfile - The user profile.
 * @returns {string[]} The permission list.
 */
const getPermissionList = (userProfile: any) => {
  if (process.env.ACCESS_PERMISSIONS == "true") {
    return userProfile?.permissions?.length > 0 ? userProfile?.permissions : [];
  } else {
    return userProfile?.user_id == "399b84c4-7e33-4461-bc91-bee560ed354d" || // agent_admin
      userProfile?.user_id == "27ec1c85-37ff-417e-8199-cc06b758702b" || // agent_level_1
      userProfile?.user_id == "c175186f-adde-46b9-912e-d29a2df86af6" // agent_level_2
      ? userProfile?.permissions
      : [
          "view_booking", // route level also done
          "create_new_booking", // book now in BE
          "generate_quote", // Add to quote in BE
          "finalize_booking",
          "transfer_booking",
          "view_supplier_info", // route level also done for suppliers
          "add_new_supplier",
          "update_supplier_info",
          "activate_inactivate_supplier",
          "update_global_supplier_markup",
          "view_supplier_agent_list", // route level also done for suppliers(confirm???) and supplier group
          "review_booking", // route level also done
          "view_invoice", // route level also done
          "download_invoice",
          "view_voucher",
          "download_voucher",
          "create_sub_agent", // Add External Agent in Manage Agents and in Profile overlay
          "view_credit_transactions", // route level also done & cta level pending if present
          "view_wallet_transactions", // route level also done & cta level pending if present
          "view_threshold_transactions", // route level also done & cta level pending if present
          "online_payment", // allow online payments for booking
          "use_credit_wallet", // allow credit/wallet payments for booking
          "view_agent_online_transactions", // route level also done for online payments; but pending in agent view onboarding
          "view_global_configuration", // view configurations page
          "edit_global_configuration", // add/edit in configurations page
          "create_internal_users", // for Add User & Edit User in Manage GRN and for Add Employee and for user management tabs
          "view_agent_supplier_mapping",
          "update_agent_supplier_mapping",
          "view_credit_note",
          "irn_update", // to allow users to view Invoices IRN / Credit Note IRN logs
          "view_purchase_and_debit_info", // to allow users to view Purchase order and debit notes
        ];
  }
};
